/**
 * components/riskTerminal/CompletedOrders.jsx
 *
 * upstox-087 — COMPLETED TODAY, to the Claude Design handoff (section 2): TIME · CONTRACT · BUY · SELL · INTENT,
 * the quantity and fill price in the BUY or the SELL lane (no side word, no colour on direction), a row that is
 * 30 px, a hover card for everything else, and a filter line (All · Buys · Sells · Rejected · Untagged) with the
 * day's charges at the right. Filters only hide — the order is the panel's, newest first — and the choice is kept
 * per account in this browser until the next session day. Desktop only; the phone's Orders tab is untouched.
 */

import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { C, FONT } from './theme';
import { IntentControl } from './controls';
import { orderCharges } from './model';
import {
  applyFilter, completedLayout, contractView, filterCounts, hoverOf, intentView, laneView, readFilter, writeFilter,
  typeWords, GREY, INK, RED, ROW_H,
} from './completed087';

const FILTER_LABEL = { all: 'All', buys: 'Buys', sells: 'Sells', rejected: 'Rejected', untagged: 'Untagged' };

/** The completed list's own width, measured (the element is held in state so it is picked up when it mounts). */
function useWidth(el, hint) {
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    if (!el) return undefined;
    const read = () => setW(el.clientWidth);
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return w || hint || 0;
}

function Ring({ words }) {
  return <span data-ring data-tip={words ? 'Expires ' + words : undefined} aria-label={words ? 'expires ' + words : undefined}
    style={{ width: '5px', height: '5px', borderRadius: '50%', border: '1px solid #585349', boxSizing: 'border-box', flex: '0 0 5px', display: 'inline-block' }} />;
}

function Row({ o, sym, nearest, lay, flag, open, onOpen, onClose, declare, onDone, initial, pinTip }) {
  const lane = laneView(o, lay.narrow);
  const view = contractView(o, sym, nearest, lay.narrow);
  const intent = intentView(o, lay.narrow);
  const tip = hoverOf(o, sym, view, lane, flag);
  const ch = orderCharges(o.charges);
  const grey = lane.rowGrey;
  const rule = lane.rule || !!flag;
  const ts = String(o.ts || '');
  const lanes = [['BUY', 'buy'], ['SELL', 'sell']];
  return (
    <div data-done-row data-state={lane.state} data-side={lane.side} data-tip={tip.text} data-tip-head={tip.head} data-tip-open={pinTip ? 'true' : undefined}
      style={{ position: 'relative', borderBottom: '1px solid #f1eee8', boxShadow: rule ? 'inset 3px 0 0 ' + RED : 'none' }}>
      <div style={{ display: 'grid', gridTemplateColumns: lay.cols, columnGap: lay.gap + 'px', alignItems: 'center', minHeight: ROW_H + 'px', padding: '0 ' + lay.pad + 'px', fontSize: lay.fs + 'px', fontVariantNumeric: 'tabular-nums' }}>
        <span data-col="time" style={{ fontSize: lay.narrow ? '10.5px' : '11px', color: grey ? GREY : C.ink4 }}>{ts.slice(11, 16)}</span>
        <span data-col="contract" data-contract={view.full} style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0, whiteSpace: 'nowrap', color: grey ? GREY : INK }}>
          <span>{view.name}</span>{view.ring && <Ring words={view.expiry} />}
        </span>
        {lanes.map(([side, col]) => (
          <span key={col} data-lane={col} className={side === lane.side && lane.reason ? 'rt-tip' : undefined}
            data-tip={side === lane.side && lane.reason ? lane.reason : undefined}
            style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: lane.weight, color: lane.color }}>
            {side === lane.side ? lane.text : ''}
          </span>
        ))}
        {lay.extra && <span data-col="type" style={{ fontSize: '10.5px', color: grey ? GREY : C.ink4, whiteSpace: 'nowrap', textAlign: 'right' }}>{typeWords(o)}</span>}
        {lay.extra && <span data-col="charges" style={{ fontSize: '10.5px', color: ch && !ch.unknown && !grey ? C.ink3 : GREY, whiteSpace: 'nowrap', textAlign: 'right' }}>{ch ? ch.text : ''}</span>}
        <span data-col="intent" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {intent.kind === 'word' && <span data-intent={intent.full} data-tip={intent.text !== intent.full ? intent.full : undefined} style={{ fontSize: '9.5px', letterSpacing: '0.1em', color: grey ? GREY : C.ink4 }}>{intent.text}</span>}
          {intent.kind === 'tag' && (
            <button type="button" data-tag-btn onClick={open ? onClose : onOpen} data-tip="Declare this order's intent — once, permanently"
              style={{ height: '20px', padding: '0 4px', background: open ? C.amberTint : '#fff', color: C.amber, border: '1px solid ' + C.amber, fontFamily: FONT, fontSize: '9px', letterSpacing: '0.1em', fontWeight: 700, cursor: 'pointer' }}>TAG</button>
          )}
        </span>
      </div>
      {open && intent.kind === 'tag' && (
        <div style={{ padding: '0 14px 8px 14px' }}>
          <IntentControl order={o} open onOpen={onOpen} onClose={onClose} declare={declare} onDone={onDone} initial={initial} />
        </div>
      )}
    </div>
  );
}

export default function CompletedOrders({
  done, nearest, sessionDate, accountKey, open, onToggle, chargesToday, symbolOf, flagOf,
  intentOpen, onIntentOpen, onIntentClose, declare, onDone, initial, widthHint, filterSeed, pinTip,
}) {
  const [filter, setFilter] = useState(() => filterSeed || readFilter(accountKey, sessionDate));
  const [el, setEl] = useState(null);
  const w = useWidth(el, widthHint);
  const lay = useMemo(() => completedLayout(w), [w]);
  // another account, or the next session day: the choice stored for THAT account and day (All when there is none)
  useEffect(() => { if (!filterSeed) setFilter(readFilter(accountKey, sessionDate)); }, [accountKey, sessionDate, filterSeed]);
  const choose = f => { setFilter(f); writeFilter(accountKey, sessionDate, f); if (!open) onToggle(); };

  const counts = filterCounts(done);
  const shown = applyFilter(done, filter);
  const chip = (f, text, color) => {
    const on = filter === f;
    return (
      <button key={f} type="button" data-filter={f} data-on={on ? 'true' : 'false'} onClick={() => choose(f)}
        style={{ background: on ? C.paper : 'transparent', border: '1px solid ' + (on ? C.line3 : 'transparent'), padding: '1px 6px', fontFamily: FONT, fontSize: '11px', color, fontWeight: on ? 600 : 400, cursor: 'pointer', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{text}</button>
    );
  };
  const rejText = FILTER_LABEL.rejected + ' ' + counts.rejected + (counts.fresh > 0 ? ' · ' + counts.fresh + ' new' : '');

  return (
    <div data-completed style={{ flex: '1 1 0', minHeight: '44px', display: 'flex', flexDirection: 'column', background: '#fff' }}>
      <div style={{ flex: '0 0 auto', background: C.paper3 }}>
        <div data-done-head style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '11px 12px 4px 12px', cursor: 'pointer' }} onClick={onToggle}>
          <div style={{ fontSize: '10px', color: C.ink5, width: '10px' }}>{open ? '▾' : '▸'}</div>
          <div style={{ fontSize: '11px', letterSpacing: '0.12em', color: C.ink2, fontWeight: 600, whiteSpace: 'nowrap', flex: '0 0 auto' }}>COMPLETED TODAY</div>
          <div style={{ flex: 1, minWidth: '6px' }} />
          <div style={{ fontSize: '11px', color: C.ink4, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }} data-tip={'Charges today · ' + chargesToday.title}>{chargesToday.text}</div>
        </div>
        <div data-done-filters style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1px 2px', padding: '0 8px 8px 29px' }}>
          {chip('all', FILTER_LABEL.all + ' ' + counts.all, C.ink2)}
          {chip('buys', FILTER_LABEL.buys + ' ' + counts.buys, C.ink2)}
          {chip('sells', FILTER_LABEL.sells + ' ' + counts.sells, C.ink2)}
          {chip('rejected', rejText, counts.fresh > 0 ? RED : C.ink2)}
          {chip('untagged', FILTER_LABEL.untagged + ' ' + counts.untagged, counts.untagged > 0 ? C.amber : C.ink4)}
        </div>
      </div>
      {open && (
        <div ref={setEl} data-done-list style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto', background: '#fff' }}>
          <div data-done-cols style={{ display: 'grid', gridTemplateColumns: lay.cols, columnGap: lay.gap + 'px', padding: '6px ' + lay.pad + 'px', fontSize: '9px', letterSpacing: lay.narrow ? '0.06em' : '0.12em', color: C.ink4, background: C.paper2, borderBottom: '1px solid ' + C.line2, position: 'sticky', top: 0, zIndex: 1 }}>
            <span>TIME</span><span>CONTRACT</span><span style={{ textAlign: 'right' }}>BUY</span><span style={{ textAlign: 'right' }}>SELL</span>
            {lay.extra && <span style={{ textAlign: 'right' }}>TYPE</span>}{lay.extra && <span style={{ textAlign: 'right' }}>CHARGES</span>}
            <span style={{ textAlign: 'right' }}>INTENT</span>
          </div>
          {!done.length && <div style={{ padding: '10px 14px', fontSize: '11px', color: C.ink4 }}>No completed orders yet.</div>}
          {!!done.length && !shown.length && <div data-done-none style={{ padding: '10px 14px', fontSize: '11px', color: C.ink4 }}>No {FILTER_LABEL[filter].toLowerCase()} orders today.</div>}
          {shown.map(o => (
            <Row key={o.upstox_order_id} o={o} sym={o.trading_symbol || symbolOf(o.instrument_key)} nearest={nearest} lay={lay} flag={flagOf(o)}
              open={intentOpen === 'o' + o.upstox_order_id} onOpen={() => onIntentOpen('o' + o.upstox_order_id)} onClose={onIntentClose}
              declare={declare} onDone={onDone} initial={initial} pinTip={pinTip === o.upstox_order_id} />
          ))}
        </div>
      )}
    </div>
  );
}
